import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Progress, cn } from '@design-system';
import { useTimedSequence } from '../hooks/use-timed-sequence';
import { CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';

interface LoadingSequenceProps {
  step: ConsumerStepId;
  title: string;
  subtitle: string;
  lines: string[];
  isReady: boolean;
  onDone: () => void;
  totalMs?: number;
  children?: ReactNode;
}

export function LoadingSequence({
  step,
  title,
  subtitle,
  lines,
  isReady,
  onDone,
  totalMs = 3800,
  children,
}: LoadingSequenceProps) {
  const { completed, progress } = useTimedSequence(lines.length, totalMs, isReady, onDone);

  return (
    <section className="flex flex-col gap-6" aria-busy="true" data-testid="consumer-loading">
      <div className="flex flex-col gap-2">
        <p className="text-primary text-xs font-semibold uppercase tracking-wide">{CONSUMER_STEPS[step].kicker}</p>
        <h1 className="text-2xl font-semibold text-gray-950 sm:text-3xl">{title}</h1>
        <p className="text-muted-foreground">{subtitle}</p>
      </div>
      {children}
      <Progress value={progress} aria-label={title} />
      <ol className="flex flex-col gap-3">
        {lines.map((line, index) => (
          <li
            key={line}
            className={cn(
              'flex items-center gap-3 text-sm transition-opacity duration-300 ease-out',
              index < completed ? 'text-gray-950 opacity-100' : 'text-muted-foreground opacity-40',
            )}
          >
            <span className="bg-primary/10 text-primary flex size-5 items-center justify-center rounded-full">
              {index < completed && <Check className="size-3" />}
            </span>
            {line}
          </li>
        ))}
      </ol>
    </section>
  );
}
